import { keyframes } from '@emotion/react';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ResetIcon from '@mui/icons-material/RestartAlt';
import SendIcon from '@mui/icons-material/Send';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, GridToolbar } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loading } from '@/core/components';

import {
  addWallets,
  calculateAverageBalance,
  getWalletBalances,
  getWallets,
  hasPendingTransfers,
  resetWallets,
  sendTokenToAddress,
  startDistribution,
  updateWalletIsValid,
  type WalletWithBalance,
} from '@/core/services/wallets';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/** 運営管理画面用グラデーション（黄→赤） */
const operatorGradient = 'linear-gradient(to right, #ffff00, #ff0000)';

export function WalletsPage() {
  const [wallets, setWallets] = useState<WalletWithBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasPending, setHasPending] = useState(false);
  const [masterMnemonic, setMasterMnemonic] = useState<string | null>(null);

  // ダイアログ状態
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addCount, setAddCount] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [distributeDialogOpen, setDistributeDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendToAddress, setSendToAddress] = useState('');
  const [sendAmount, setSendAmount] = useState('');

  // 操作中フラグ
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [walletsData, pending] = await Promise.all([getWallets(), hasPendingTransfers()]);

      // マスターウォレットのニーモニックを保持
      const masterWallet = walletsData.find((w) => w.index === 0);
      if (masterWallet?.mnemonic) {
        setMasterMnemonic(masterWallet.mnemonic.join(' '));
      }

      // 残高取得
      const walletsWithBalance = await getWalletBalances(walletsData);
      setWallets(walletsWithBalance);
      setHasPending(pending);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 有効/無効トグル
  const handleToggleValid = useCallback(async (index: number, isValid: boolean) => {
    try {
      await updateWalletIsValid(index, isValid);
      setWallets((prev) => prev.map((w) => (w.index === index ? { ...w, isValid } : w)));
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新に失敗しました');
    }
  }, []);

  // ウォレット追加
  const handleAddWallets = async () => {
    if (!masterMnemonic) {
      setError('マスターウォレットのニーモニックがありません');
      return;
    }

    const count = parseInt(addCount, 10);
    if (Number.isNaN(count) || count <= 0) {
      setError('追加数を正しく入力してください');
      return;
    }

    setIsProcessing(true);
    try {
      const maxIndex = Math.max(...wallets.map((w) => w.index));
      await addWallets(masterMnemonic, maxIndex + 1, count);
      setAddDialogOpen(false);
      setAddCount('');
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ウォレット追加に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // ウォレットリセット
  const handleReset = async () => {
    setIsProcessing(true);
    try {
      await resetWallets();
      setResetDialogOpen(false);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'リセットに失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // 残高配分
  const handleDistribute = async () => {
    const masterWallet = wallets.find((w) => w.index === 0);
    if (!masterWallet) {
      setError('マスターウォレットがありません');
      return;
    }

    setIsProcessing(true);
    try {
      await startDistribution(wallets, masterWallet.walletAddress);
      setDistributeDialogOpen(false);
      setHasPending(true);
      alert('トークンのtransferを開始しました。');
    } catch (err) {
      setError(err instanceof Error ? err.message : '配分開始に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // 任意アドレスへ送金
  const handleSendToken = async () => {
    const masterWallet = wallets.find((w) => w.index === 0);
    if (!masterWallet) {
      setError('マスターウォレットがありません');
      return;
    }

    if (!sendToAddress) {
      setError('送金先ウォレットを選択してください');
      return;
    }

    const amount = parseFloat(sendAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      setError('送金額を正しく入力してください');
      return;
    }

    setIsProcessing(true);
    try {
      await sendTokenToAddress(masterWallet.walletAddress, sendToAddress.trim(), amount);
      setSendDialogOpen(false);
      setSendToAddress('');
      setSendAmount('');
      setHasPending(true);
      alert('トークンの送金を開始しました。');
    } catch (err) {
      setError(err instanceof Error ? err.message : '送金開始に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const averageBalance = useMemo(() => calculateAverageBalance(wallets), [wallets]);
  const totalBalance = useMemo(() => wallets.reduce((sum, w) => sum + parseFloat(w.balance), 0), [wallets]);
  const validCount = useMemo(() => wallets.filter((w) => w.isValid).length, [wallets]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'index',
        headerName: 'Index',
        width: 80,
      },
      {
        field: 'walletAddress',
        headerName: 'アドレス',
        width: 400,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'balance',
        headerName: '残高 (ETH)',
        width: 150,
        renderCell: (params) => <Typography variant="body2">{params.value} ETH</Typography>,
      },
      {
        field: 'nonce',
        headerName: 'Nonce',
        width: 80,
      },
      {
        field: 'isValid',
        headerName: '有効',
        width: 100,
        renderCell: (params) => (
          <Switch
            checked={params.value}
            onChange={(e) => handleToggleValid(params.row.index, e.target.checked)}
            size="small"
          />
        ),
      },
    ],
    [handleToggleValid]
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3, animation: `${fadeInUp} 0.6s ease-out` }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {hasPending && (
        <Alert severity="info" sx={{ mb: 3, animation: `${fadeInUp} 0.6s ease-out` }}>
          配分処理が進行中です。完了まで残高配分は無効になっています。
        </Alert>
      )}

      {/* 統計カード */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 100, px: 2, py: 1, borderRadius: '8px', backgroundColor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">
              ウォレット数
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {wallets.length}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 100, px: 2, py: 1, borderRadius: '8px', backgroundColor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">
              有効
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {validCount}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 140, px: 2, py: 1, borderRadius: '8px', backgroundColor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">
              合計残高
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {totalBalance.toFixed(4)} ETH
            </Typography>
          </Box>
          <Box sx={{ minWidth: 140, px: 2, py: 1, borderRadius: '8px', backgroundColor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">
              平均残高
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {averageBalance.toFixed(5)} ETH
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              sx={{
                borderRadius: '16px',
                border: '2px solid transparent',
                backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                color: '#1D1B20',
                '&:hover': {
                  backgroundImage: `linear-gradient(#f5f5f5, #f5f5f5), ${operatorGradient}`,
                },
              }}
            >
              更新
            </Button>
            <Button
              variant="contained"
              startIcon={<ShuffleIcon />}
              onClick={() => setDistributeDialogOpen(true)}
              disabled={hasPending || wallets.length === 0}
              sx={{
                background: operatorGradient,
                color: '#1D1B20',
                borderRadius: '16px',
                '&:hover': { background: operatorGradient, filter: 'brightness(0.95)' },
                '&:disabled': { background: operatorGradient, opacity: 0.6 },
              }}
            >
              残高配分
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => setSendDialogOpen(true)}
              disabled={hasPending || wallets.length === 0}
              sx={{
                background: operatorGradient,
                color: '#1D1B20',
                borderRadius: '16px',
                '&:hover': { background: operatorGradient, filter: 'brightness(0.95)' },
                '&:disabled': { background: operatorGradient, opacity: 0.6 },
              }}
            >
              送金
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              sx={{
                borderRadius: '16px',
                border: '2px solid transparent',
                backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                color: '#1D1B20',
                '&:hover': {
                  backgroundImage: `linear-gradient(#f5f5f5, #f5f5f5), ${operatorGradient}`,
                },
              }}
            >
              追加
            </Button>
            <Button
              variant="outlined"
              startIcon={<ResetIcon />}
              onClick={() => setResetDialogOpen(true)}
              sx={{
                borderRadius: '16px',
                borderColor: 'error.main',
                color: 'error.main',
                '&:hover': { backgroundColor: 'error.light', borderColor: 'error.main' },
              }}
            >
              リセット
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* データグリッド */}
      <Paper
        sx={{
          height: 600,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          animation: `${fadeInUp} 0.6s ease-out 0.2s both`,
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'action.hover',
            borderBottom: '2px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
          '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <DataGrid
          rows={wallets}
          columns={columns}
          getRowId={(row) => row.index}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* ウォレット追加ダイアログ */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>ウォレット追加</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>追加するウォレット数を入力してください。</DialogContentText>
          <TextField
            autoFocus
            label="追加数"
            type="number"
            fullWidth
            value={addCount}
            onChange={(e) => setAddCount(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setAddDialogOpen(false)}
            sx={{
              borderRadius: '16px',
              border: '2px solid transparent',
              backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              color: '#1D1B20',
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleAddWallets}
            disabled={isProcessing}
            sx={{
              background: operatorGradient,
              color: '#1D1B20',
              borderRadius: '16px',
              '&:hover': { background: operatorGradient, filter: 'brightness(0.95)' },
              '&:disabled': { background: operatorGradient, opacity: 0.6 },
            }}
          >
            {isProcessing ? <CircularProgress size={20} color="inherit" /> : '追加'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* リセット確認ダイアログ */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>ウォレットリセット</DialogTitle>
        <DialogContent>
          <DialogContentText>
            マスターウォレット（index=0）以外のすべてのウォレットを削除します。この操作は取り消せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setResetDialogOpen(false)}
            sx={{
              borderRadius: '16px',
              border: '2px solid transparent',
              backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              color: '#1D1B20',
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleReset}
            disabled={isProcessing}
            sx={{
              background: 'error.main',
              color: '#fff',
              borderRadius: '16px',
              '&:hover': { background: 'error.dark' },
              '&:disabled': { opacity: 0.6 },
            }}
          >
            {isProcessing ? <CircularProgress size={20} color="inherit" /> : 'リセット'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 残高配分確認ダイアログ */}
      <Dialog open={distributeDialogOpen} onClose={() => setDistributeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>残高配分</DialogTitle>
        <DialogContent>
          <DialogContentText>
            各ウォレットの残高を <strong>{averageBalance.toFixed(5)} ETH</strong> に均します。よろしいですか？
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDistributeDialogOpen(false)}
            sx={{
              borderRadius: '16px',
              border: '2px solid transparent',
              backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              color: '#1D1B20',
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleDistribute}
            disabled={isProcessing}
            sx={{
              background: operatorGradient,
              color: '#1D1B20',
              borderRadius: '16px',
              '&:hover': { background: operatorGradient, filter: 'brightness(0.95)' },
              '&:disabled': { background: operatorGradient, opacity: 0.6 },
            }}
          >
            {isProcessing ? <CircularProgress size={20} color="inherit" /> : '配分開始'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* トークン送金ダイアログ */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>トークン送金</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            マスターウォレットから選択したウォレットにトークンを送金します。
          </DialogContentText>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>送金先ウォレット</InputLabel>
            <Select value={sendToAddress} label="送金先ウォレット" onChange={(e) => setSendToAddress(e.target.value)}>
              {wallets
                .filter((w) => w.index !== 0)
                .map((w) => (
                  <MenuItem key={w.index} value={w.walletAddress}>
                    #{w.index} - {w.walletAddress.slice(0, 10)}...{w.walletAddress.slice(-8)} ({w.balance} ETH)
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            label="送金額 (ETH)"
            type="number"
            inputProps={{ step: '0.0001', min: '0' }}
            fullWidth
            value={sendAmount}
            onChange={(e) => setSendAmount(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSendDialogOpen(false)}
            sx={{
              borderRadius: '16px',
              border: '2px solid transparent',
              backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              color: '#1D1B20',
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleSendToken}
            disabled={isProcessing}
            sx={{
              background: operatorGradient,
              color: '#1D1B20',
              borderRadius: '16px',
              '&:hover': { background: operatorGradient, filter: 'brightness(0.95)' },
              '&:disabled': { background: operatorGradient, opacity: 0.6 },
            }}
          >
            {isProcessing ? <CircularProgress size={20} color="inherit" /> : '送金'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
